import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Trophy, Calendar, Hash, User, BookOpen, Download, Loader2, GraduationCap } from 'lucide-react';
import { verifyCertificate, downloadCertificate } from '../../services/certificates';

export default function CertificateVerify() {
    const { number } = useParams();
    const [state, setState] = useState('loading'); // loading | valid | invalid
    const [cert, setCert] = useState(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        verifyCertificate(number)
            .then(r => { setCert(r.data.certificate); setState('valid'); })
            .catch(() => setState('invalid'));
    }, [number]);

    const handleDownload = async () => {
        if (!cert) return;
        setDownloading(true);
        try {
            const res = await downloadCertificate(cert.id);
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `sertifikat-${cert.certificate_number}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { /* ignore */ }
        finally { setDownloading(false); }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col items-center justify-center p-4">

            {/* Logo / Brand */}
            <Link to="/" className="flex items-center gap-2 mb-8 group">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
                    <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-white tracking-wide">SmartEduLab</span>
            </Link>

            {/* Card */}
            <div className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden">

                {/* Top accent bar */}
                <div className={`h-2 w-full ${state === 'valid' ? 'bg-gradient-to-r from-green-400 to-emerald-500' : state === 'invalid' ? 'bg-gradient-to-r from-red-400 to-rose-500' : 'bg-gradient-to-r from-blue-400 to-indigo-500'}`} />

                <div className="p-8">
                    {/* ── Loading ── */}
                    {state === 'loading' && (
                        <div className="flex flex-col items-center gap-4 py-8">
                            <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                            <p className="text-gray-500 text-sm">Sertifikat tekshirilmoqda...</p>
                        </div>
                    )}

                    {/* ── Invalid ── */}
                    {state === 'invalid' && (
                        <div className="flex flex-col items-center gap-4 py-6 text-center">
                            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center">
                                <XCircle className="w-12 h-12 text-red-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Sertifikat topilmadi</h2>
                            <p className="text-gray-500 dark:text-gray-400 text-sm max-w-xs">
                                <span className="font-mono bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-xs">{number}</span> raqamli sertifikat mavjud emas yoki noto'g'ri
                            </p>
                            <Link to="/" className="mt-2 text-blue-600 hover:underline text-sm">Bosh sahifaga qaytish</Link>
                        </div>
                    )}

                    {/* ── Valid ── */}
                    {state === 'valid' && cert && (
                        <div className="flex flex-col items-center text-center gap-5">
                            {/* Status badge */}
                            <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center ring-4 ring-green-100">
                                <CheckCircle className="w-12 h-12 text-green-500" />
                            </div>

                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Sertifikat tasdiqlandi</h2>
                                <p className="text-green-600 dark:text-green-400 text-sm font-medium">✓ Haqiqiy va to'g'ri</p>
                            </div>

                            {/* Divider */}
                            <div className="w-full border-t border-dashed border-gray-200 dark:border-gray-700" />

                            {/* Trophy icon */}
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-md">
                                <Trophy className="w-9 h-9 text-white" />
                            </div>

                            {/* Info rows */}
                            <div className="w-full space-y-3 text-left">
                                <InfoRow icon={<User className="w-4 h-4 text-blue-500" />} label="Talaba" value={cert.user?.name} />
                                <InfoRow icon={<BookOpen className="w-4 h-4 text-purple-500" />} label="Kurs" value={cert.course?.title} />
                                <InfoRow icon={<Hash className="w-4 h-4 text-gray-400" />} label="Sertifikat raqami" value={cert.certificate_number} mono />
                                <InfoRow icon={<Calendar className="w-4 h-4 text-green-500" />} label="Berilgan sana" value={new Date(cert.issued_at).toLocaleDateString('uz-UZ', { year: 'numeric', month: 'long', day: 'numeric' })} />
                                {cert.grade && (
                                    <InfoRow icon={<GraduationCap className="w-4 h-4 text-yellow-500" />} label="Baho" value={cert.grade} />
                                )}
                            </div>

                            {/* Divider */}
                            <div className="w-full border-t border-gray-200 dark:border-gray-700" />

                            {/* Download button */}
                            <button
                                onClick={handleDownload}
                                disabled={downloading}
                                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-md transition disabled:opacity-60"
                            >
                                {downloading
                                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Yuklanmoqda...</>
                                    : <><Download className="w-4 h-4" /> PDF yuklab olish</>
                                }
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-8 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 text-center">
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        SmartEduLab tomonidan berilgan rasmiy sertifikat · <Link to="/" className="text-blue-500 hover:underline">smartedulab.uz</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

function InfoRow({ icon, label, value, mono = false }) {
    return (
        <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3">
            <div className="shrink-0">{icon}</div>
            <div className="min-w-0">
                <p className="text-xs text-gray-400 dark:text-gray-500 leading-none mb-0.5">{label}</p>
                <p className={`text-sm font-semibold text-gray-900 dark:text-white truncate ${mono ? 'font-mono' : ''}`}>{value}</p>
            </div>
        </div>
    );
}
